/**
 * Shared formatting + categorization helpers used by every marketplace
 * surface (browse, detail, manage, admin). Keeping these pure so they
 * can be reused without React state.
 */

import { format, formatDistanceToNow } from '../../utils/datetime';
import { useSelector } from 'react-redux';

export const KIND_LABELS = {
  realtime:   'Real-time',
  historical: 'Historical',
  both:       'Real-time + Historical',
};

// Update-frequency dropdown for the publish wizard. Values are stable
// machine labels stored on the listing; the FE displays ``label``.
// "custom" lets sellers type a free-form string when none of the
// presets fit.
export const UPDATE_FREQUENCY_OPTIONS = [
  { value: 'real_time',         label: 'Real-time (continuous)' },
  { value: 'every_minute',      label: 'Every minute' },
  { value: 'every_5_minutes',   label: 'Every 5 minutes' },
  { value: 'every_15_minutes',  label: 'Every 15 minutes' },
  { value: 'hourly',            label: 'Hourly' },
  { value: 'every_6_hours',     label: 'Every 6 hours' },
  { value: 'daily',             label: 'Daily' },
  { value: 'weekly',            label: 'Weekly' },
  { value: 'monthly',           label: 'Monthly' },
  { value: 'custom',            label: 'Other / Custom…' },
];

// Per-bucket spacing of the underlying time-series.
export const DATA_INTERVAL_OPTIONS = [
  { value: '1s',     label: '1 second' },
  { value: '10s',    label: '10 seconds' },
  { value: '1m',     label: '1 minute' },
  { value: '5m',     label: '5 minutes' },
  { value: '15m',    label: '15 minutes' },
  { value: '1h',     label: '1 hour' },
  { value: '1d',     label: '1 day' },
  { value: '1w',     label: '1 week' },
  { value: '1mo',    label: '1 month' },
  { value: 'custom', label: 'Other / Custom…' },
];

// Service-level commitment shown on the listing detail page.
export const SLA_OPTIONS = [
  { value: '99.99', label: '99.99% uptime (≤ 1 hr / year downtime)' },
  { value: '99.9',  label: '99.9% uptime  (≤ 9 hrs / year)' },
  { value: '99.5',  label: '99.5% uptime  (≤ 2 days / year)' },
  { value: '99',    label: '99% uptime' },
  { value: 'best_effort', label: 'Best effort' },
  { value: 'custom', label: 'Other / Custom…' },
];

// "Realtime / Historical / Both" - same triple stored on the listing.
export const KIND_OPTIONS = [
  { value: 'realtime',   label: 'Real-time' },
  { value: 'historical', label: 'Historical' },
  { value: 'both',       label: 'Real-time + Historical' },
];

// Pretty label for any update_frequency value (preset OR custom).
// Used by the listing detail page so the seller sees "Hourly"
// instead of the underlying ``hourly`` slug.
export const updateFrequencyLabel = (value) => {
  if (!value) return '';
  const opt = UPDATE_FREQUENCY_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : value;
};
export const dataIntervalLabel = (value) => {
  if (!value) return '';
  const opt = DATA_INTERVAL_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : value;
};
export const slaLabel = (value) => {
  if (!value) return '';
  const opt = SLA_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : value;
};

export const formatCredits = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '0';
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });
};

export const dailyFromMonthly = (monthlyCredits) => {
  if (!monthlyCredits || monthlyCredits <= 0) return 0;
  // 30.4375 = average days per month over the Gregorian cycle
  return monthlyCredits / 30.4375;
};

/**
 * Pricing model for a listing. Returns one of:
 *   - 'free'         — both prices = 0
 *   - 'onetime'      — onetime > 0, monthly = 0 (perpetual after pay)
 *   - 'subscription' — onetime = 0, monthly > 0 (classic recurring)
 *   - 'hybrid'       — both > 0 (upfront fee + recurring)
 */
export const pricingModelOf = (listing) => {
  const monthly = Number(listing?.price_monthly_credits || 0);
  const onetime = Number(listing?.price_onetime_credits || 0);
  if (monthly <= 0 && onetime <= 0) return 'free';
  if (monthly <= 0 && onetime > 0)  return 'onetime';
  if (monthly > 0  && onetime <= 0) return 'subscription';
  return 'hybrid';
};

export const PRICING_MODEL_LABELS = {
  free:         'Free',
  onetime:      'One-time fee',
  subscription: 'Subscription',
  hybrid:       'One-time + Subscription',
};

/**
 * Compose a single human-readable price summary for a card / row.
 * Always reports in USD - the storage unit (credits) is an
 * implementation detail buyers never see.
 *
 * Examples:
 *   - "Free"
 *   - "$0.12 / month"
 *   - "$1.00 one-time"
 *   - "$1.00 one-time + $0.12 / month"
 */
export const priceSummary = (listing, usdPerCredit = 0.01) => {
  const monthly = Number(listing?.price_monthly_credits || 0);
  const onetime = Number(listing?.price_onetime_credits || 0);
  if (monthly <= 0 && onetime <= 0) return 'Free';
  const parts = [];
  if (onetime > 0) parts.push(`${formatUSD(onetime * usdPerCredit)} one-time`);
  if (monthly > 0) parts.push(`${formatUSD(monthly * usdPerCredit)} / month`);
  return parts.join(' + ');
};

// ---------------------------------------------------------------------------
// USD / credits conversion helpers
// ---------------------------------------------------------------------------

/**
 * Convert a credits amount to its USD value at the given rate.
 * Returns a number; pair with ``formatUSD`` for display.
 *
 * The rate (``usdPerCredit``) comes from the BE
 * ``GET /v1/settings/public`` endpoint and is cached in Redux at
 * ``state.settings_public.response.credit.value_usd``. Use the
 * ``useUsdRate`` hook below in components to avoid threading it
 * through props.
 */
export const usdFromCredits = (credits, usdPerCredit = 0.01) => {
  const c = Number(credits) || 0;
  const r = Number(usdPerCredit) || 0;
  return c * r;
};

/**
 * Convert a USD amount back to credits at the given rate. Used by
 * the publish/edit forms so sellers can enter dollar values that the
 * BE stores as integer credits.
 */
export const creditsFromUsd = (usd, usdPerCredit = 0.01) => {
  const u = Number(usd) || 0;
  const r = Number(usdPerCredit) || 0;
  if (r <= 0) return 0;
  return u / r;
};

/**
 * Format a USD number for display.
 *   formatUSD(0)     -> "$0.00"
 *   formatUSD(1.5)   -> "$1.50"
 *   formatUSD(1234)  -> "$1,234.00"
 *   formatUSD(0.005) -> "$0.005"  (sub-cent precision when needed)
 */
/**
 * React hook that returns the current USD-per-credit rate from
 * Redux. Defaults to ``0.01`` until the BE response lands so
 * components never render ``$NaN`` on first paint. Marketplace
 * surfaces should always read the rate via this hook so changes
 * to the topup pricing flow through automatically.
 */
export const useUsdRate = () => useSelector(
  (s) => s?.settings_public?.response?.credit?.value_usd ?? 0.01,
);

/**
 * Calling user's organization memberships from
 * ``GET /v1/settings/public``. Each entry: ``{ uid, name, role }``.
 * The marketplace uses this to conditionally show org-only
 * affordances (the Public/Org toggle, the "publish to org" radio in
 * the wizard, etc.). Returns ``[]`` for users with no org.
 */
export const useViewerOrganizations = () => useSelector(
  (s) => s?.settings_public?.response?.viewer?.organizations ?? [],
);

// Verified-provider / publish-capability state for the current
// workspace (GET /marketplace/seller-status, fetched via the
// sellerStatus saga). ``null`` until the fetch lands.
export const useSellerStatus = () => useSelector(
  (s) => s?.marketplace_seller_status?.response ?? null,
);

/**
 * Should the "publish to marketplace" affordance be rendered at all?
 *
 * Selling PUBLICLY requires the workspace's organization to be a
 * verified provider with the right role (``can_publish`` from
 * /seller-status). Org members without verification can still share
 * signals INTERNALLY (org-visibility listings are ungated), so any
 * org membership keeps the affordance visible — the publish wizard
 * then hides the "Public marketplace" option.
 */
export const useCanOpenPublishFlow = () => {
  const status = useSellerStatus();
  const orgs = useViewerOrganizations();
  return Boolean(status?.can_publish) || orgs.length > 0;
};

export const formatUSD = (amount, opts = {}) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '$0.00';
  const abs = Math.abs(n);
  // Show 4 decimals for very small amounts so $0.0005 doesn't read
  // as "$0.00" - relevant for per-day pricing of cheap signals.
  const fractionDigits = opts.fractionDigits ?? (
    abs > 0 && abs < 0.01 ? 4 : 2
  );
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

export const niceDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    const ageDays = (Date.now() - dt.getTime()) / 86400000;
    return ageDays < 30
      ? formatDistanceToNow(dt, { addSuffix: true })
      : format(dt, 'MMM d, yyyy');
  } catch (_e) {
    return '—';
  }
};

/**
 * Compact relative time string without the noisy "about" prefix
 * date-fns adds for hour/day units. "about 19 hours ago" -> "19h ago",
 * "less than a minute ago" -> "<1m ago", etc. Used in the
 * marketplace listings table where row height is precious.
 */
export const compactRelativeDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return formatDistanceToNow(dt, { addSuffix: true })
      .replace(/^about\s+/, '')
      .replace(/^less than\s+/, '<')
      .replace(/^over\s+/, '>')
      .replace(/^almost\s+/, '~');
  } catch (_e) {
    return '—';
  }
};

/**
 * Full timestamp string for hover tooltips: "Mon, Jan 15, 2024
 * 3:42 PM PST". Pairs with ``compactRelativeDate`` so the cell shows
 * "19 hours ago" while the hover surfaces the precise time.
 */
export const fullDateTime = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return format(dt, "EEE, MMM d, yyyy 'at' h:mm a");
  } catch (_e) {
    return '';
  }
};

/**
 * Build a {slug -> category} map from the flat list returned by
 * /marketplace/categories. The map is used to render breadcrumbs +
 * walk children for the cascading picker.
 */
export const buildCategoryIndex = (categories = []) => {
  const bySlug = {};
  const childrenByParent = {};
  (categories || []).forEach((c) => {
    bySlug[c.slug] = c;
    const parent = c.parent_slug || '__root__';
    if (!childrenByParent[parent]) childrenByParent[parent] = [];
    childrenByParent[parent].push(c);
  });
  Object.keys(childrenByParent).forEach((p) => {
    childrenByParent[p].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  });
  return { bySlug, childrenByParent };
};

export const categoryBreadcrumb = (slug, bySlug) => {
  const trail = [];
  let cur = bySlug[slug];
  while (cur) {
    trail.unshift(cur);
    cur = cur.parent_slug ? bySlug[cur.parent_slug] : null;
  }
  return trail;
};

export const categoryLabel = (slug, bySlug) => {
  const t = categoryBreadcrumb(slug, bySlug);
  return t.length ? t.map((c) => c.name).join(' › ') : slug;
};
